import { preloadImage } from '@hooks/useImagePreloader';
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import { usePaymentConfig } from '@providers/PaymentConfigProvider';
import { PaymentProvider } from '@providers/PaymentProvider/PaymentProvider';
import { QuizProvider } from '@providers/QuizProvider/QuizProvider';
import { IMAGE_PATHS, WEB_APP_IMAGE_PATHS } from '@utils/imagePaths';
import { redirectToNativeBrowser } from '@utils/redirectToNativeBrowser';
import { useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';

import { useConfig } from '@providers/ConfigRedirectProvider';
import { initFacebookPixel } from '@services/fbPixel';
import Router from './Router';

export enum Routes {
  MAIN = '/',
  ONBOARDING = '/onboarding',
  ONBOARDING2 = '/onboarding2',
  OFFERS = '/offers',
  SUBSCRIPTION = '/subscription',
  ACCESS_PRODUCT = '/access-product',
  SIGN_IN = '/sign-in',
  QUIZ = '/quiz',
  PHOTOLAB_QUIZ = '/photolab-quiz',
  ETHNICITY_QUIZ = '/ethnicity-quiz',
  COMPARISON_QUIZ = '/comp-quiz',
  LOOKALIKE_QUIZ = '/lookalike-quiz',
  LOOKALIKE_REPORT = '/lookalike-report',
  COMPARISION_REPORTS = '/reports/:selectedComparison/:reportId',
  LOOKALIKE_REPORTS = '/lookalike-reports/:reportId',
  WEB_APP_PREVIEW = '/preview',
  WEBAPP = '/webapp',
  WEBAPP_MAIN = '/webapp/main',
  WEBAPP_PHOTOLAB = '/webapp/photolab',
  WEBAPP_PHOTORECOVERY = '/webapp/photorecovery',
  WEBAPP_PHOTORECOVERY_RESULTS = '/webapp/photorecovery-results',
  WEBAPP_PHOTOANIMATION = '/webapp/photoanimation',
  WEBAPP_PHOTOANIMATION_RESULTS = '/webapp/photoanimation-results',
  WEBAPP_PROFILE = '/webapp/profile',
  WEBAPP_ETHNICITY = '/webapp/myethnicity',
  WEBAPP_REPORTS_HISTORY = '/webapp/reports',
  WEBAPP_MANAGE_SUBSCRIPTIONS = '/webapp/manage-subscriptions',
  WEBAPP_FAMILY_TREE = '/webapp/family-tree',
  WEBAPP_FACE_COMPARE_HISTORY = '/webapp/face-compare',
  WEBAPP_CELEBRITY_LOOKALIKE_HISTORY = '/webapp/celebrity-lookalike',
  WEBAPP_GENERAL_REPORTS_HISTORY = '/webapp/general-reports',
  UNSUBSCRIBE = '/unsubscription',
  UNSUBSCRIBE_EMAIL = '/unsubscribe-with-email',
  ACCESS_RECOVERY = '/access-recovery',
  ACTIVATE = '/activate',
  SHARE = '/webapp/share',
  SHARE_RECOVERY_RESULTS = '/webapp/share-recovery-results',
}

function App() {
  const config = useConfig();
  const configParams = config.params;

  useEffect(() => {
    // Meta Pixel initialization.
    //
    // Legacy mytraits.ai: pixel ID comes from dynamic config (configParams.fb)
    //   loaded via ConfigRedirectProvider from config.mytraits.ai.
    //
    // mydnatest24.com: there is no per-config setup — fallback to the
    //   hardcoded default pixel ID (1614282913201177) so the Meta Pixel
    //   actually loads. Without this branch, configParams.fb is undefined
    //   and the pixel never initializes on mydnatest24, breaking ALL
    //   Meta event tracking.
    if (configParams.fb) {
      initFacebookPixel(Number(configParams.fb));
    } else if (
      typeof window !== 'undefined' &&
      window.location.host.includes('mydnatest24.com')
    ) {
      initFacebookPixel(1614282913201177);
    }
  }, [configParams.fb]);

  redirectToNativeBrowser(configParams);

  useEffect(() => {
    // SKIP the legacy mytraits.ai image preload list when running on
    // mydnatest24 — those photolab / quiz / webapp images aren't used
    // on the mydnatest24 onboarding flow and were costing the user
    // ~24 MB of wasted bandwidth on the landing page. The mydna flow
    // has its own targeted preloader in services/assetPreloader.ts
    // that fires from inside MydnaOnboardingProvider.
    const isMydnaHost =
      typeof window !== 'undefined' &&
      window.location.host.includes('mydnatest24.com');
    if (isMydnaHost) return;

    const imagesToLoad = () => {
      switch (true) {
        case window.location.href.includes(Routes.WEBAPP): {
          return WEB_APP_IMAGE_PATHS;
        }
        default: {
          return IMAGE_PATHS;
        }
      }
    };

    const prelodImg = async () => {
      const assets = imagesToLoad();
      const promises = assets.map(asset => {
        if (asset.endsWith('.mp4') || asset.endsWith('.webm')) {
          return fetch(asset)
            .then(res => res.blob())
            .then(() => { })
            .catch(() => { });
        } else {
          return preloadImage(asset);
        }
      });
      const promiseSettledResult = await Promise.allSettled(promises);
      const rejected = promiseSettledResult.find(
        promise => promise.status === 'rejected',
      );
      if (rejected) {
        console.error('Some images was not loaded: ', rejected);
      } else {
        // console.log('Images loaded');
      }
    };

    prelodImg();
  }, []);

  const { selectedPlan, isMultiCurrency } = usePaymentConfig();

  return (
    <BrowserRouter>
      <QuizProvider>
        <PaymentProvider>
          <PayPalScriptProvider
            options={{
              clientId: import.meta.env.VITE_PAYPAL_CLIENT_ID || '',
              vault: true,
              currency: isMultiCurrency ? selectedPlan.currency : 'USD',
            }}
          >
            <Router />
          </PayPalScriptProvider>
        </PaymentProvider>
      </QuizProvider>
    </BrowserRouter>
  );
}

export default App;
